import type { FleetCompanyOnboardingStatus } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useMemo } from 'react';
import useUser from '../useEntity/useUser';
import { useFleetCompanyOnboardingStatus } from '../useFleetContracts';

/**
 * Cache gravado por `apps/dashboard/src/providers/FeatureFlagsProvider.tsx` (dono das escritas —
 * aqui é somente leitura). Chave `onboardingStatus_<fleetId>`, shape `{ value, timestamp }`,
 * TTL de 24h. Usado como `initialData` para render instantâneo sem flicker no boot.
 */
const ONBOARDING_STATUS_STORAGE_KEY = 'onboardingStatus';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface CachedOnboardingStatus {
  value: Pick<FleetCompanyOnboardingStatus, 'hasAnyOnboardingContract' | 'hasAnyContractSignedInLast90Days'>;
  timestamp: number;
}

const readCachedOnboardingStatus = (fleetId: string): CachedOnboardingStatus | null => {
  if (!fleetId || typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(`${ONBOARDING_STATUS_STORAGE_KEY}_${fleetId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedOnboardingStatus;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.timestamp !== 'number' || !parsed.value || typeof parsed.value !== 'object') return null;
    if (typeof parsed.value.hasAnyContractSignedInLast90Days !== 'boolean') return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

interface UseIsOnboardingFleetResult {
  /**
   * `true` somente quando a frota tem contrato assinado há menos de 90 dias
   * (`companyOnboardingStatus.hasAnyContractSignedInLast90Days`). Erro, timeout ou ausência de
   * dado caem em `false` (home normal, sem tela quebrada).
   */
  isOnboardingFleet: boolean;
  /** `true` apenas enquanto a query de status ainda não tem dado nem erro. */
  isLoading: boolean;
}

/**
 * Regra central do épico AE-2434: o "Guia de configuração" (/basic-value) e seus acessos
 * (sidebar, menu do usuário e hero da home) só aparecem para frotas em onboarding — contrato
 * assinado há menos de 90 dias, sinal vindo do backend via `companyOnboardingStatus`.
 */
const useIsOnboardingFleet = (): UseIsOnboardingFleetResult => {
  const { session } = useUser();
  const fleetId = session?.fleet.id ?? '';

  const cached = useMemo(() => readCachedOnboardingStatus(fleetId), [fleetId]);

  // `initialDataUpdatedAt` mantém a idade real do cache: pinta instantâneo e o react-query faz
  // background-refresh quando passa do staleTime — staleness limitada para frotas cruzando os 90 dias.
  const { data, isPending } = useFleetCompanyOnboardingStatus(fleetId, {
    enabled: !!fleetId,
    retry: 1,
    ...(cached ? { initialData: cached.value, initialDataUpdatedAt: cached.timestamp } : {}),
  });

  return {
    isOnboardingFleet: data?.hasAnyContractSignedInLast90Days === true,
    isLoading: !!fleetId && isPending,
  };
};

export default useIsOnboardingFleet;
